import classNames from 'classnames';
import type { Icon } from '@phosphor-icons/react';
import DotsSixVertical from '@components/Icons/DotsSixVertical';
import { useState, useRef, useEffect } from 'react';
import CaretCircleDown from '@components/Icons/CaretCircleDown';
import s from './CollapsibleArea.module.scss';

type Props = {
  title: string;
  openTitle?: string;
  className?: string;
  Icon?: {
    Component: Icon;
    ComponentOpen?: Icon;
    color?: string;
    size?: number;
  };
  caretPosition?: 'left' | 'right';
  open?: boolean;
  description?: string;
  hideCaret?: boolean;
  CustomIcon?: React.ReactNode;
  draggable?: boolean;
  onlyToggleOnCaret?: boolean;
  clipTitle?: boolean;
};

const CollapsibleArea: React.FC<Props> = ({
  CustomIcon,
  caretPosition = 'right',
  children,
  className,
  description,
  open,
  title,
  openTitle,
  Icon,
  hideCaret,
  draggable = false,
  onlyToggleOnCaret = false,
  clipTitle = false,
}) => {
  const [isOpen, setIsOpen] = useState(open);
  const [isUserTriggered, setIsUserTriggered] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const toggleListener = (): void => {
      setIsOpen(detailsRef.current?.open);
    };

    const detailsElement = detailsRef.current;
    detailsElement?.addEventListener('toggle', toggleListener);

    return () => {
      detailsElement?.removeEventListener('toggle', toggleListener);
    };
  }, [setIsOpen]);

  useEffect(() => {
    if (isOpen && isUserTriggered && detailsRef.current) {
      detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setIsUserTriggered(false);
    }
  }, [isOpen, isUserTriggered]);

  const handleToggleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsUserTriggered(true);
    detailsRef.current?.click();
  };

  const IconComponent = Icon?.ComponentOpen && isOpen ? Icon.ComponentOpen : Icon?.Component;

  return (
    <details ref={detailsRef} className={classNames(s['collapsible-area'], className)} open={isOpen}>
      <summary
        onClick={(e) => {
          if (onlyToggleOnCaret) {
            e.preventDefault();
          } else {
            e.stopPropagation();
            setIsUserTriggered(true);
          }
        }}
      >
        {caretPosition === 'left' && !hideCaret && (
          <CaretCircleDown
            weight="fill"
            size={16}
            onClick={handleToggleClick}
            style={{ cursor: 'pointer' }}
          />
        )}
        <div>
          {draggable && <DotsSixVertical size={20} />}
          {Icon && IconComponent && (
            <IconComponent
              size={Icon.size || 20}
              color={Icon.color || 'var(--color-gray-100)'}
            />
          )}
          {CustomIcon && <div className={s.customIcon}>{CustomIcon}</div>}
          <div className={s.titleAndDescription}>
            <span title={clipTitle ? title : undefined} className={clipTitle ? s.ellipsis : undefined}>
              {(isOpen && openTitle) ? openTitle : title}
            </span>
            {description && <span className="caption">{description}</span>}
          </div>
        </div>
        {caretPosition === 'right' && !hideCaret && (
          <CaretCircleDown
            weight="fill"
            size={16}
            onClick={handleToggleClick}
            style={{ cursor: 'pointer' }}
          />
        )}
      </summary>
      {children}
    </details>
  );
};

export default CollapsibleArea;
